import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTiers } from "@/lib/useTiers";
import { api, formatApiError } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle2, Lock, Loader2, Tag, X } from "lucide-react";

function fmtMoney(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

export default function CheckoutModal({ open, onOpenChange, tierKey }) {
  const [agree, setAgree] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState("");
  const { user, setUser } = useAuth();
  const { tiers } = useTiers();
  const tier = tiers.find((t) => t.tier_key === tierKey || t.key === tierKey);

  if (!tier) return null;

  const applyPromo = async () => {
    const code = (promoInput || "").trim();
    if (!code) return;
    setPromoChecking(true);
    setPromoError("");
    try {
      const { data } = await api.post("/promo/validate", {
        code,
        kind: "tier",
        item_id: tier.tier_key || tier.key,
      });
      setPromo(data);
    } catch (e) {
      setPromo(null);
      setPromoError(formatApiError(e?.response?.data?.detail) || "Invalid code");
    } finally {
      setPromoChecking(false);
    }
  };

  const clearPromo = () => {
    setPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  const handleCheckout = async () => {
    if (!agree) {
      toast.error("Please accept the disclaimer to continue.");
      return;
    }
    if (!user) {
      window.location.href = "/register";
      return;
    }
    setRedirecting(true);
    try {
      await api.post("/auth/disclaimer", { accepted: true }).catch(() => {});
      const payload = {
        tier_key: tier.tier_key || tier.key,
        origin_url: window.location.origin,
      };
      if (promo?.code) payload.promo_code = promo.code;
      const { data } = await api.post("/payments/paypal/create-tier-order", payload);
      if (data.free_granted) {
        toast.success(`Access unlocked with code ${data.promo_code}!`);
        try {
          const me = await api.get("/auth/me");
          setUser(me.data);
        } catch (e) {}
        onOpenChange(false);
        window.location.href = "/dashboard";
        return;
      }
      if (data.approve_url) {
        window.location.href = data.approve_url;
      } else {
        toast.error("PayPal checkout failed to open. Please try again.");
        setRedirecting(false);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Checkout failed");
      setRedirecting(false);
    }
  };

  const close = () => {
    setAgree(false);
    clearPromo();
    onOpenChange(false);
  };

  const payLabel = promo
    ? (promo.is_free
        ? "Unlock now (100% off)"
        : `Pay ${fmtMoney(promo.final_cents)} with PayPal`)
    : `Pay ${tier.price} with PayPal`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); onOpenChange(v); }}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">
            Upgrade to <span className="text-brand-gradient">{tier.name}</span>
          </DialogTitle>
          <DialogDescription className="text-white/60">{tier.sub}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-gradient-to-br from-[#FF8A00]/10 to-[#E52E71]/5 border border-[#FF8A00]/20 p-5 my-3">
          <div className="flex items-baseline justify-between">
            <div className="font-display text-5xl">
              {promo ? fmtMoney(promo.final_cents) : tier.price}
            </div>
            <div className="text-xs text-white/50">{tier.cadence}</div>
          </div>
          {promo && (
            <div className="mt-1 text-xs text-white/50">
              <span className="line-through mr-2">{fmtMoney(promo.original_cents)}</span>
              <span className="text-[#FF8A00]">− {fmtMoney(promo.discount_cents)} saved</span>
            </div>
          )}
          <ul className="mt-4 space-y-2">
            {tier.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                <CheckCircle2 size={16} className="mt-0.5 text-[#FF8A00] shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-2" data-testid="promo-field-wrap">
          {promo ? (
            <div className="flex items-center justify-between rounded-md border border-[#FF8A00]/30 bg-[#FF8A00]/5 px-3 py-2 text-sm" data-testid="promo-applied">
              <div className="flex items-center gap-2">
                <Tag size={14} className="text-[#FF8A00]" />
                <span className="font-mono">{promo.code}</span>
                <span className="text-white/60">applied</span>
              </div>
              <button
                onClick={clearPromo}
                className="text-white/40 hover:text-white"
                data-testid="promo-clear-btn"
                aria-label="Remove code"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-stretch gap-2">
              <input
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder="Promo code (optional)"
                className="flex-1 h-9 rounded-md bg-white/5 border border-white/10 px-3 text-sm uppercase placeholder:text-white/30 placeholder:normal-case focus:outline-none focus:border-[#FF8A00]/40"
                data-testid="promo-input"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
              />
              <Button
                type="button"
                onClick={applyPromo}
                disabled={promoChecking || !promoInput.trim()}
                data-testid="promo-apply-btn"
                className="h-9 px-4 rounded-md bg-white/10 hover:bg-white/15 text-sm"
              >
                {promoChecking ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
              </Button>
            </div>
          )}
          {promoError && <div className="mt-1 text-[11px] text-red-400" data-testid="promo-error">{promoError}</div>}
        </div>

        <label className="flex items-start gap-3 text-xs text-white/60 cursor-pointer mt-3" data-testid="disclaimer-checkbox-wrap">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-0.5 accent-[#FF8A00]"
            data-testid="disclaimer-checkbox"
          />
          <span>
            I understand this content is for educational purposes only and not medical advice.
            I will consult a qualified healthcare provider before starting any protocol.
          </span>
        </label>
        <div className="mt-2 flex items-center gap-2 text-[11px] text-white/40">
          <Lock size={12} /> {promo?.is_free ? "No payment required — access granted instantly." : "Secured by PayPal. Pay with PayPal, Venmo, or any card."}
        </div>
        <Button
          onClick={handleCheckout}
          disabled={!agree || redirecting}
          data-testid="confirm-checkout-btn"
          className="w-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90 text-white rounded-full py-6 text-base"
        >
          {redirecting ? (
            <><Loader2 className="mr-2 animate-spin" size={16} /> {promo?.is_free ? "Unlocking…" : "Redirecting to PayPal…"}</>
          ) : (
            <>{payLabel}</>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
